import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPatch, apiDelete, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';

interface SessionRow {
  id: string;
  is_current: boolean;
  ip: string | null;
  user_agent: string | null;
  issued_at: string | null;
  expires_at: string;
}

// Rough device label from user agent. Errs on the side of being friendly,
// not forensically accurate.
function deviceLabel(ua: string | null): string {
  if (!ua) return 'Unknown device';
  const lower = ua.toLowerCase();
  let os = 'Unknown';
  if (lower.includes('windows'))      os = 'Windows';
  else if (lower.includes('mac os'))  os = 'macOS';
  else if (lower.includes('iphone'))  os = 'iPhone';
  else if (lower.includes('ipad'))    os = 'iPad';
  else if (lower.includes('android')) os = 'Android';
  else if (lower.includes('linux'))   os = 'Linux';

  let browser = 'browser';
  if (lower.includes('firefox'))      browser = 'Firefox';
  else if (lower.includes('edg/'))    browser = 'Edge';
  else if (lower.includes('chrome'))  browser = 'Chrome';
  else if (lower.includes('safari'))  browser = 'Safari';

  return `${browser} on ${os}`;
}

export function PortalSettings() {
  const navigate = useNavigate();
  const { user, refresh, signOut } = useAuth();
  const [name, setName] = useState(user?.name ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const [sessions, setSessions] = useState<SessionRow[] | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [sessionErr, setSessionErr] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setPhone(user.phone ?? '');
    }
  }, [user]);

  useEffect(() => {
    apiGet<{ sessions: SessionRow[] }>('/api/auth/sessions')
      .then((r) => setSessions(r.sessions))
      .catch((err: Error) => setSessionErr(err instanceof ApiError ? err.serverMessage : err.message));
  }, []);

  const profileDirty = (user && (name !== user.name || phone !== (user.phone ?? '')));

  async function saveProfile(e: FormEvent) {
    e.preventDefault();
    if (!profileDirty || saving) return;
    setSaving(true);
    setProfileMsg(null);
    try {
      const patch: Record<string, string> = {};
      if (name !== user?.name) patch.name = name;
      if (phone !== (user?.phone ?? '')) patch.phone = phone;
      await apiPatch('/api/users/me', patch);
      await refresh();
      setProfileMsg({ kind: 'ok', text: 'Profile updated.' });
    } catch (err) {
      setProfileMsg({ kind: 'err', text: err instanceof ApiError ? err.serverMessage : 'Could not save profile.' });
    } finally {
      setSaving(false);
    }
  }

  async function revokeSession(id: string) {
    if (revoking) return;
    setRevoking(id);
    setSessionErr(null);
    try {
      await apiDelete(`/api/auth/sessions/${id}`);
      const current = sessions?.find((s) => s.id === id)?.is_current;
      if (current) {
        // Server already cleared the cookie. Mirror that in the SPA.
        await signOut();
        navigate('/portal/sign-in', { replace: true });
        return;
      }
      setSessions((prev) => (prev ?? []).filter((s) => s.id !== id));
    } catch (err) {
      setSessionErr(err instanceof ApiError ? err.serverMessage : 'Could not revoke session.');
    } finally {
      setRevoking(null);
    }
  }

  return (
    <>
      <div>
        <h1 className="portal-h1">Settings</h1>
        <p className="portal-sub">Your profile, active sessions, and account preferences.</p>
      </div>

      <form className="card" style={{ marginTop: 24, padding: 'clamp(20px, 3vw, 32px)' }} onSubmit={saveProfile}>
        <h3 style={{ fontSize: 17, margin: 0 }}>Profile</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 18 }}>
          <div className="field">
            <label>Full name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field">
            <label>Direct phone</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+44 …" />
          </div>
          <div className="field">
            <label>Corporate email</label>
            <input value={user?.email ?? ''} disabled />
            <div className="hint">Sign-in codes go here. Contact your engagement lead to change.</div>
          </div>
          <div className="field">
            <label>Role</label>
            <input value={user?.contact_role ?? ''} disabled />
            <div className="hint">Authorised contact role is set by Compliance.</div>
          </div>
        </div>

        {profileMsg && (
          <div style={{ marginTop: 14, fontSize: 13, color: profileMsg.kind === 'err' ? 'var(--red)' : 'var(--accent)' }}>
            {profileMsg.text}
          </div>
        )}

        <div style={{ marginTop: 18 }}>
          <button className="btn accent" type="submit" disabled={!profileDirty || saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>

      <div className="card" style={{ marginTop: 20, padding: 'clamp(20px, 3vw, 32px)' }}>
        <h3 style={{ fontSize: 17, margin: 0 }}>Company</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 18 }}>
          <div className="field">
            <label>Client ID</label>
            <input value={user?.client_id ?? '—'} disabled />
          </div>
          <div className="field">
            <label>Account status</label>
            <input value={user?.status ?? '—'} disabled />
          </div>
        </div>
        <div className="hint" style={{ marginTop: 8 }}>
          Company name, registration and KYC details are managed by Compliance. Contact your
          engagement lead if any of them needs to change.
        </div>
      </div>

      <div className="card" style={{ marginTop: 20, padding: 'clamp(20px, 3vw, 32px)' }}>
        <h3 style={{ fontSize: 17, margin: 0 }}>Active sessions</h3>
        <p style={{ color: 'var(--fg-soft)', fontSize: 13.5, marginTop: 6, lineHeight: 1.6, marginBottom: 14 }}>
          Devices where you're currently signed in. Revoke any session you don't recognise.
        </p>
        {sessionErr && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 8 }}>{sessionErr}</div>}
        {sessions === null && <div className="hint">Loading…</div>}
        {sessions !== null && sessions.length === 0 && <div className="hint">No active sessions found.</div>}
        {sessions !== null && sessions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sessions.map((s) => (
              <div
                key={s.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 14px',
                  border: '1px solid var(--rule)',
                  borderRadius: 'var(--r)',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <div style={{ fontSize: 14 }}>
                    {deviceLabel(s.user_agent)}
                    {s.is_current && <span className="chip accent" style={{ marginLeft: 8 }}>This device</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)', marginTop: 2 }}>
                    Signed in {s.issued_at ? s.issued_at.slice(0, 10) : '—'} · expires {s.expires_at.slice(0, 10)}
                  </div>
                </div>
                <button
                  className="btn ghost"
                  type="button"
                  disabled={revoking === s.id}
                  onClick={() => revokeSession(s.id)}
                >
                  {revoking === s.id ? 'Revoking…' : s.is_current ? 'Sign out here' : 'Revoke'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
